import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Popup, useMap, Circle, CircleMarker, Tooltip, Polygon, Polyline, Marker } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import MarkerClusterGroup from 'react-leaflet-cluster'; // Correct import for the new library
import './MapComponent.css';
// import { v4 as uuidv4 } from 'uuid'; // Unused for now
import 'leaflet-control-geocoder';
import { db } from '../../firebase-config';
import { serverTimestamp, doc, setDoc, deleteDoc } from 'firebase/firestore';
import GeofenceDrawer from '../GeofenceDrawer';
import GeofenceNamingModal from '../GeofenceNamingModal';
import GeofenceManager from '../GeofenceManager';
import ZoomIndependentMarker from '../ZoomIndependentMarker';
import DeviceMarker from '../DeviceMarker'; // Import the new component
import SwimZoneLayer from '../SwimZoneLayer';
import CityStateBoundaryLayer from '../CityStateBoundaryLayer';
import { setShouldNavigateToSelectedDevice } from '../../store/slices/uiSlice';
import { clearHistory } from '../../store/slices/deviceHistorySlice';
import { pointInGeoJSON } from '../../utils/geospatial';
import { findSosLgBandDevices, isSosOutsideGeofences, getSosAnchorLatLng, hasOpenSosIncident } from '../../utils/lgBandSos';

// Clustering functionality removed for stability

// Error boundary component for map
class MapErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Map Error Boundary caught an error:', error, errorInfo);
        console.error('Error stack:', error.stack);
        console.error('Component stack:', errorInfo.componentStack);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    height: '100vh',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5'
                }}>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <h3>Map Error</h3>
                        <p>There was an issue loading the map. Please refresh the page.</p>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#004aad',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Refresh Page
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Custom function to create the cluster icon
const createClusterCustomIcon = function (cluster) {
    const count = cluster.getChildCount();
    let sizeClass = 'small';
    if (count > 25) {
      sizeClass = 'large';
    } else if (count > 10) {
      sizeClass = 'medium';
    }
  
    return new L.DivIcon({
      html: `<div><span>${count}</span></div>`,
      className: `marker-cluster marker-cluster-${sizeClass}`,
      iconSize: new L.Point(48, 48),
    });
};

// Fix for default marker icon issue with Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Fix for _leaflet_pos error - override the problematic getPosition function
const originalGetPosition = L.DomUtil.getPosition;
L.DomUtil.getPosition = function (el) {
    try {
        if (!el || !el.parentNode) {
            console.warn('Element not in DOM, returning default position');
            return new L.Point(0, 0);
        }
        return originalGetPosition.call(this, el);
    } catch (error) {
        console.warn('getPosition error caught:', error);
        return new L.Point(0, 0);
    }
};

// Clustering functionality removed


const iconSize = [64, 64];
const iconAnchor = [32, 64];
const popupAnchor = [0, -60];

const mobileIcon = new L.Icon({
    iconUrl: '/mobile_logo.png',
    iconSize,
    iconAnchor,
    popupAnchor
});

const tritonIcon = new L.Icon({
    iconUrl: '/triton_3.png',
    iconSize,
    iconAnchor,
    popupAnchor
});

const aurusIcon = new L.Icon({
    iconUrl: '/aurus.png',
    iconSize,
    iconAnchor,
    popupAnchor
});

const ebikeIcon = new L.Icon({
    iconUrl: '/ebike.png',
    iconSize,
    iconAnchor,
    popupAnchor
});

const vehicleIcon = new L.Icon({
    iconUrl: '/vehicle.png',
    iconSize,
    iconAnchor,
    popupAnchor
});

const boatIcon = new L.Icon({
    iconUrl: '/boat.png', // Using boat.png instead of sailboat.png for consistency
    iconSize: [56, 72], // Increased size but maintaining proportions
    iconAnchor: [28, 72], // Center horizontally, anchor at bottom
    popupAnchor: [0, -72]
});

const truckIcon = new L.Icon({
    iconUrl: '/truck.png',
    iconSize: [60, 68], // Slightly larger than default
    iconAnchor: [30, 68], // Center horizontally, anchor at bottom
    popupAnchor: [0, -68]
});

const bikeIcon = new L.Icon({
    iconUrl: '/bike.png',
    iconSize: [64, 64], // Standard size to match other icons
    iconAnchor: [32, 64], // Center horizontally, anchor at bottom
    popupAnchor: [0, -64]
});

const jeepIcon = new L.Icon({
    iconUrl: '/jeep.png',
    iconSize,
    iconAnchor,
    popupAnchor
});

const atvIcon = new L.Icon({
    iconUrl: '/vehicle.png', // Using vehicle icon for ATV as it's more appropriate than jeep
    iconSize,
    iconAnchor,
    popupAnchor
});

const seaHorseIcon = new L.Icon({
    iconUrl: '/s_icon.png',
    iconSize,
    iconAnchor,
    popupAnchor
});

// const buildingIcon = new L.Icon({
//   iconUrl: '/building-icon.png', // You need to add this icon to your public folder
//   iconSize: [48, 48],
//   iconAnchor: [24, 48],
//   popupAnchor: [0, -48],
// });

const officeIcon = new L.Icon({
    iconUrl: '/pinpoint_tower.png', // Using a generic building icon for now
    iconSize: [48, 48],
    iconAnchor: [24, 48],
    popupAnchor: [0, -48],
});

// const towerIcon = new L.Icon({
//   iconUrl: '/pinpoint_tower.png',
//   iconSize: [48, 48],
//   iconAnchor: [24, 48],
//   popupAnchor: [0, -48],
// });

const towerIconImage = '/pinpoint_tower.png';

const createTowerIcon = (tower, showNameplates) => {
    const nameplateHTML = showNameplates ? `<div class="device-nameplate">${tower.name}</div>` : '';

    return new L.DivIcon({
        html: `<div class="device-marker zoom-independent custom-tower-icon">
      ${nameplateHTML}
      <div class="icon-wrapper">
        <img src="${towerIconImage}" alt="${tower.name}" />
      </div>
    </div>`,
        className: showNameplates ? 'show-nameplates' : '',
        iconSize: [64, 64], // Square size for better anchoring
        iconAnchor: [32, 64], // Anchor at bottom center of the icon
        popupAnchor: [0, -64], // Popup appears above the icon
    });
};

// Helper function to truncate device names intelligently
const truncateDeviceName = (name, maxLength = 12) => {
    if (!name || name.length <= maxLength) return name;

    // Try to truncate at word boundaries
    const words = name.split(' ');
    if (words.length > 1) {
        let truncated = '';
        for (const word of words) {
            if ((truncated + word).length <= maxLength) {
                truncated += (truncated ? ' ' : '') + word;
            } else {
                break;
            }
        }
        if (truncated.length > 0) return truncated;
    }

    // Fallback to character truncation with ellipsis
    return name.substring(0, maxLength - 1) + '…';
};

/** Lat/lng for map follow (supports LG Band + merged device shapes). */
function getDeviceLatLngForFollow(device) {
    if (!device) return null;
    let lat = device.last_latitude ?? device.latitude ?? device.location?.latitude ?? device.coordinates?.lat;
    let lng = device.last_longitude ?? device.longitude ?? device.location?.longitude ?? device.coordinates?.lng;
    if (typeof lat === 'string') lat = parseFloat(lat);
    if (typeof lng === 'string') lng = parseFloat(lng);
    if (lat === null || lat === undefined || lng === null || lng === undefined) return null;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    if (lat === 0 && lng === 0) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng };
}

/**
 * While an LG Band (or adafruit-mapped) device is selected, keep the map panned to its latest position.
 * Other device types are ignored.
 */
// Follow-mode tuning (LG Band live tracking)
const FOLLOW_MIN_MOVE_M = 15;          // ignore GPS jitter below this distance
const FOLLOW_USER_PAUSE_MS = 20 * 1000; // after the user drags/zooms, don't auto-recentre for this long
const LG_BAND_LOCATE_ZOOM = 16;         // clustering is off from 16: the band AND its neighbours stay visible

function haversineMeters(aLat, aLng, bLat, bLng) {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(bLat - aLat);
    const dLng = toRad(bLng - aLng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}

function FollowSelectedLgBand({ devices, selectedDevice }) {
    const map = useMap();
    const lastCoordKeyRef = useRef('');
    const lastDeviceIdRef = useRef('');
    const panTimerRef = useRef(null);
    const lastPannedRef = useRef(null);      // {lat,lng} we last centred on
    const userPausedUntilRef = useRef(0);   // epoch ms; follow is suspended until then

    // 2. Pause following while the user is working the map (drag / wheel / touch / zoom buttons).
    useEffect(() => {
        if (!map) return undefined;
        const pause = () => { userPausedUntilRef.current = Date.now() + FOLLOW_USER_PAUSE_MS; };
        const container = map.getContainer?.();
        map.on('dragstart', pause);
        container?.addEventListener('wheel', pause, { passive: true });
        container?.addEventListener('touchstart', pause, { passive: true });
        const zoomCtl = container?.querySelector?.('.leaflet-control-zoom');
        zoomCtl?.addEventListener('click', pause);
        return () => {
            map.off('dragstart', pause);
            container?.removeEventListener('wheel', pause);
            container?.removeEventListener('touchstart', pause);
            zoomCtl?.removeEventListener('click', pause);
        };
    }, [map]);

    const followTarget = useMemo(() => {
        if (!selectedDevice?.device_id) return null;
        const t = selectedDevice.device_type;
        if (t !== 'lg_band' && t !== 'adafruit') return null;
        const live = Array.isArray(devices) ? devices.find((d) => d.device_id === selectedDevice.device_id) : null;
        // While an SOS is open, the view stays on the SOS point: don't chase the live position.
        if (hasOpenSosIncident(live || selectedDevice)) return null;
        const ll = getDeviceLatLngForFollow(live || selectedDevice);
        if (!ll) return null;
        return { lat: ll.lat, lng: ll.lng, deviceId: selectedDevice.device_id };
    }, [selectedDevice, devices]);

    useEffect(() => {
        if (panTimerRef.current) {
            clearTimeout(panTimerRef.current);
            panTimerRef.current = null;
        }

        if (!map || !followTarget) {
            lastCoordKeyRef.current = '';
            lastDeviceIdRef.current = '';
            return undefined;
        }

        if (followTarget.deviceId !== lastDeviceIdRef.current) {
            // Re-selecting (or selecting another band) always re-centres and clears any pause.
            lastCoordKeyRef.current = '';
            lastPannedRef.current = null;
            lastDeviceIdRef.current = followTarget.deviceId;
            userPausedUntilRef.current = 0;
        }
        const key = `${followTarget.lat.toFixed(6)},${followTarget.lng.toFixed(6)}`;
        if (key === lastCoordKeyRef.current) {
            return undefined;
        }
        // 1. Follow only on real movement: ignore GPS jitter below FOLLOW_MIN_MOVE_M.
        if (lastPannedRef.current) {
            const moved = haversineMeters(lastPannedRef.current.lat, lastPannedRef.current.lng, followTarget.lat, followTarget.lng);
            if (moved < FOLLOW_MIN_MOVE_M) {
                return undefined;
            }
        }
        // 2. The user is interacting with the map: leave it alone for now.
        if (Date.now() < userPausedUntilRef.current) {
            return undefined;
        }

        panTimerRef.current = setTimeout(() => {
            panTimerRef.current = null;
            try {
                if (!map.getContainer?.()?.parentNode) return;
                lastCoordKeyRef.current = key;
                lastPannedRef.current = { lat: followTarget.lat, lng: followTarget.lng };
                map.panTo([followTarget.lat, followTarget.lng], {
                    animate: true,
                    duration: 0.38,
                    easeLinearity: 0.22,
                });
            } catch (e) {
                console.warn('[FollowSelectedLgBand] panTo failed:', e);
            }
        }, 90);

        return () => {
            if (panTimerRef.current) {
                clearTimeout(panTimerRef.current);
                panTimerRef.current = null;
            }
        };
    }, [map, followTarget]);

    return null;
}

function MapZoom({ position, zoomLevel = 18, forceNavigate = false }) {
    const map = useMap();
    const dispatch = useDispatch();
    const hasNavigatedRef = React.useRef(false);
    const lastPositionRef = React.useRef(null);

    useEffect(() => {
        console.log('🗺️ [MapZoom] Effect triggered:', {
            hasPosition: !!position,
            position: position,
            positionType: Array.isArray(position) ? 'array' : typeof position,
            positionLength: Array.isArray(position) ? position.length : 'N/A',
            forceNavigate,
            hasNavigated: hasNavigatedRef.current,
            lastPosition: lastPositionRef.current
        });

        // Only navigate if forceNavigate is true AND we have a valid position
        if (forceNavigate && position && map && Array.isArray(position) && position.length === 2) {
            const [lat, lng] = position;
            const positionKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;

            // Validate coordinates
            if (typeof lat === 'number' && typeof lng === 'number' &&
                !isNaN(lat) && !isNaN(lng) &&
                lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 &&
                !(lat === 0 && lng === 0)) {

                // Navigate if we haven't navigated to this position yet, or if position changed
                const shouldNavigate = !hasNavigatedRef.current || lastPositionRef.current !== positionKey;

                if (shouldNavigate) {
                    hasNavigatedRef.current = true;
                    lastPositionRef.current = positionKey;

                    try {
                        // Ensure the map is still valid before trying to use it
                        if (map.getContainer() && map.getContainer().parentNode) {
                            console.log('🗺️ [MapZoom] ✅ STARTING NAVIGATION to device location:', {
                                lat,
                                lng,
                                zoomLevel,
                                forceNavigate,
                                positionKey
                            });

                            // Stop any ongoing animations, then fly to the new position
                            map.stop();
                            const animationDuration = 1.2;

                            // Use a small delay to ensure map is ready
                            setTimeout(() => {
                                try {
                            map.flyTo([lat, lng], zoomLevel, {
                                        duration: animationDuration,
                                easeLinearity: 0.25,
                            });

                                    console.log('🗺️ [MapZoom] ✅ Navigation animation started');

                                    // Reset navigation flag after animation completes
                                    setTimeout(() => {
                                        console.log('✅ [MapZoom] Navigation completed, resetting navigation flag');
                                        dispatch(setShouldNavigateToSelectedDevice(false));
                                        // Reset hasNavigated so we can navigate again if needed
                                        hasNavigatedRef.current = false;
                                    }, animationDuration * 1000 + 200); // Wait for animation + buffer
                                } catch (navError) {
                                    console.error('🗺️ [MapZoom] Error during flyTo:', navError);
                                    dispatch(setShouldNavigateToSelectedDevice(false));
                                    hasNavigatedRef.current = false;
                                }
                            }, 50); // Small delay to ensure map is ready
                        } else {
                            console.warn('🗺️ [MapZoom] Map container not ready for navigation');
                            // Reset flag even if navigation fails
                            dispatch(setShouldNavigateToSelectedDevice(false));
                            hasNavigatedRef.current = false;
                        }
                    } catch (error) {
                        console.error('🗺️ [MapZoom] Map navigation failed:', error);
                        // Reset flag on error
                        dispatch(setShouldNavigateToSelectedDevice(false));
                        hasNavigatedRef.current = false;
                    }
                } else {
                    console.log('🗺️ [MapZoom] Already navigated to this position, resetting flag');
                    // Already navigated, just reset the flag
                    setTimeout(() => {
                        dispatch(setShouldNavigateToSelectedDevice(false));
                    }, 100);
                }
            } else {
                console.warn('🗺️ [MapZoom] ❌ Invalid coordinates for navigation:', {
                    lat,
                    lng,
                    latType: typeof lat,
                    lngType: typeof lng,
                    latIsNaN: isNaN(lat),
                    lngIsNaN: isNaN(lng),
                    isZero: lat === 0 && lng === 0,
                    latRange: lat < -90 || lat > 90,
                    lngRange: lng < -180 || lng > 180
                });
                // Reset flag if coordinates are invalid
                dispatch(setShouldNavigateToSelectedDevice(false));
                hasNavigatedRef.current = false;
            }
        } else {
            if (forceNavigate && !position) {
                console.warn('🗺️ [MapZoom] ⚠️ forceNavigate is true but no position provided - waiting for position...');
                // Don't reset flag here - wait for position to be calculated
            } else if (forceNavigate && !map) {
                console.warn('🗺️ [MapZoom] ⚠️ forceNavigate is true but map not available');
                dispatch(setShouldNavigateToSelectedDevice(false));
            } else if (forceNavigate && (!Array.isArray(position) || position.length !== 2)) {
                console.warn('🗺️ [MapZoom] ⚠️ forceNavigate is true but position is invalid:', {
                    isArray: Array.isArray(position),
                    length: Array.isArray(position) ? position.length : 'N/A',
                    position: position
                });
                dispatch(setShouldNavigateToSelectedDevice(false));
            } else if (!forceNavigate) {
                // Reset hasNavigated when forceNavigate becomes false
                hasNavigatedRef.current = false;
                lastPositionRef.current = null;
            }
        }
    }, [position, zoomLevel, map, forceNavigate, dispatch]);

    return null;
}

// Helper function to check if a point is inside a polygon using ray casting algorithm
const isPointInPolygon = (point, polygon) => {
    // Validate inputs
    if (!point || !polygon || !Array.isArray(polygon) || polygon.length < 3) {
        return false;
    }

    const [lat, lng] = point;
    if (typeof lat !== 'number' || typeof lng !== 'number') {
        return false;
    }

    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        // Handle different polygon data formats
        let latI, lngI, latJ, lngJ;

        if (typeof polygon[i] === 'object' && polygon[i] !== null) {
            // Format: [{lat: x, lng: y}, ...]
            latI = polygon[i].lat;
            lngI = polygon[i].lng;
        } else if (Array.isArray(polygon[i])) {
            // Format: [[lat, lng], ...]
            [latI, lngI] = polygon[i];
        } else {
            continue; // Skip invalid data
        }

        if (typeof polygon[j] === 'object' && polygon[j] !== null) {
            // Format: [{lat: x, lng: y}, ...]
            latJ = polygon[j].lat;
            lngJ = polygon[j].lng;
        } else if (Array.isArray(polygon[j])) {
            // Format: [[lat, lng], ...]
            [latJ, lngJ] = polygon[j];
        } else {
            continue; // Skip invalid data
        }

        // Validate coordinates
        if (typeof latI !== 'number' || typeof lngI !== 'number' ||
            typeof latJ !== 'number' || typeof lngJ !== 'number') {
            continue;
        }

        if (((lngI > lng) !== (lngJ > lng)) &&
            (lat < (latJ - latI) * (lng - lngI) / (lngJ - lngI) + latI)) {
            inside = !inside;
        }
    }
    return inside;
};

// Smart Label Manager Component - Simplified without clustering
const SmartLabelManager = ({ devices, showNameplates, map }) => {
    const [visibleLabels, setVisibleLabels] = React.useState(new Set());
    const [zoomLevel, setZoomLevel] = React.useState(10);

    React.useEffect(() => {
        if (!map || !showNameplates) return;

        const updateLabelVisibility = () => {
            const currentZoom = map.getZoom();
            setZoomLevel(currentZoom);

            // Show labels at reasonable zoom levels
            if (currentZoom < 12) {
                setVisibleLabels(new Set());
                return;
            }

            // Calculate which labels to show based on proximity
            const newVisibleLabels = new Set();
            const labelPositions = new Map();
            const minDistance = 50; // Minimum distance between labels in pixels

            // Sort devices by priority (running devices first, then by name)
            const sortedDevices = [...devices].sort((a, b) => {
                const aStatus = a.status?.toLowerCase();
                const bStatus = b.status?.toLowerCase();

                // Running devices get priority
                if (aStatus === 'running' && bStatus !== 'running') return -1;
                if (bStatus === 'running' && aStatus !== 'running') return 1;

                // Then sort by name for consistency
                const aName = a.displayName || a.name || a.device_id || '';
                const bName = b.displayName || b.name || b.device_id || '';
                return aName.localeCompare(bName);
            });

            sortedDevices.forEach(device => {
                if (!device.last_latitude || !device.last_longitude) return;

                const point = map.latLngToContainerPoint([device.last_latitude, device.last_longitude]);
                const deviceId = device.unique_key || device.device_id || device.name;

                // Check if this label conflicts with existing ones
                let canShow = true;
                for (const [existingId, existingPoint] of labelPositions) {
                    const distance = Math.sqrt(
                        Math.pow(point.x - existingPoint.x, 2) +
                        Math.pow(point.y - existingPoint.y, 2)
                    );

                    if (distance < minDistance) {
                        canShow = false;
                        break;
                    }
                }

                if (canShow) {
                    newVisibleLabels.add(deviceId);
                    labelPositions.set(deviceId, point);
                }
            });

            setVisibleLabels(newVisibleLabels);
        };

        // Debounce label updates to prevent performance issues
        let timeoutId;
        const debouncedUpdate = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(updateLabelVisibility, 150);
        };

        // Update on zoom and move
        map.on('zoomend moveend', debouncedUpdate);
        updateLabelVisibility(); // Initial update

        return () => {
            map.off('zoomend moveend', debouncedUpdate);
            clearTimeout(timeoutId);
        };
    }, [map, showNameplates, devices]);

    return { visibleLabels, zoomLevel };
};


// Calculate bearing in degrees (0=North, 90=East) between two [lat,lng] points
const calcBearing = ([lat1, lng1], [lat2, lng2]) => {
  const toRad = d => d * Math.PI / 180;
  const toDeg = r => r * 180 / Math.PI;
  const dLng = toRad(lng2 - lng1);
  const φ1 = toRad(lat1), φ2 = toRad(lat2);
  const y = Math.sin(dLng) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

// Approximate geodesic distance between two [lat,lng] points in meters.
const calcDistanceMeters = ([lat1, lng1], [lat2, lng2]) => {
  const R = 6371000;
  const toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const makeArrowIcon = (bearing) => L.divIcon({
  className: '',
  // SVG arrow points NORTH by default; rotate by computed bearing.
  html: `<div style="width:22px;height:22px;transform:rotate(${bearing}deg);transform-origin:50% 50%;display:flex;align-items:center;justify-content:center;">
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2 L18 15 L12 12 L6 15 Z" fill="#004aad" stroke="#ffffff" stroke-width="1.6" stroke-linejoin="round"></path>
    </svg>
  </div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const makeStartIcon = () => L.divIcon({
  className: '',
  html: `<div style="background:linear-gradient(135deg,#16a34a,#22c55e);color:#fff;font-size:10px;font-weight:700;border-radius:50%;width:20px;height:20px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 3px 8px rgba(0,0,0,0.28)">S</div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const makeEndIcon = () => L.divIcon({
  className: '',
  html: `<div style="background:linear-gradient(135deg,#1d4ed8,#2563eb);color:#fff;font-size:10px;font-weight:700;border-radius:50%;width:20px;height:20px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 3px 8px rgba(0,0,0,0.28)">E</div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const makeTrailPointLabelIcon = (index, timeLabel) => L.divIcon({
  className: '',
  html: `<div style="transform:translateY(-8px);background:rgba(255,255,255,0.95);color:#0f172a;font-size:10px;font-weight:600;border:1px solid #93c5fd;border-radius:6px;padding:2px 6px;box-shadow:0 2px 6px rgba(15,23,42,0.18);white-space:nowrap;">#${index} · ${timeLabel}</div>`,
  iconSize: [92, 20],
  iconAnchor: [46, 20],
});

const DeviceHistoryTrail = () => {
  const dispatch = useDispatch();
  const { activeDeviceId, points, trailMode, loading } = useSelector(state => state.deviceHistory);
  const selectedDevice = useSelector(state => state.devices.selectedDevice);

  const selectedType = String(selectedDevice?.device_type || '').toLowerCase();
  const selectedIsLgBand = selectedType === 'lg_band' || selectedType === 'adafruit';
  const selectedMatchesTrail = !!(
    selectedDevice?.device_id != null &&
    activeDeviceId != null &&
    String(selectedDevice.device_id) === String(activeDeviceId)
  );
  const isLiveTrail = trailMode === 'live';

  if (!activeDeviceId || points.length === 0) return null;

  // Restrict only the live-session trail to the currently selected LG Band.
  // History mode should render for all devices as soon as points are available.
  if (isLiveTrail && (!selectedIsLgBand || !selectedMatchesTrail)) return null;

  // Guard against malformed history points that can crash Leaflet
  const validPoints = points.filter((p) => {
    if (!p) return false;
    const lat = typeof p.lat === 'string' ? parseFloat(p.lat) : p.lat;
    const lng = typeof p.lng === 'string' ? parseFloat(p.lng) : p.lng;
    return Number.isFinite(lat) && Number.isFinite(lng)
      && lat >= -90 && lat <= 90
      && lng >= -180 && lng <= 180;
  }).map((p) => ({
    ...p,
    lat: typeof p.lat === 'string' ? parseFloat(p.lat) : p.lat,
    lng: typeof p.lng === 'string' ? parseFloat(p.lng) : p.lng,
  }));

  if (validPoints.length === 0) return null;

  const positions = validPoints.map(p => [p.lat, p.lng]);
  const formatTrailPointTime = (value) => {
    if (!value) return 'No time';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'No time';
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  // Place an arrow marker at the midpoint of each meaningful segment.
  const arrowMarkers = [];
  const MIN_ARROW_SEGMENT_METERS = 12;
  for (let i = 0; i < positions.length - 1; i++) {
    const segmentDistance = calcDistanceMeters(positions[i], positions[i + 1]);
    if (segmentDistance < MIN_ARROW_SEGMENT_METERS) continue;

    const [lat1, lng1] = positions[i];
    const [lat2, lng2] = positions[i + 1];
    const midLat = (lat1 + lat2) / 2;
    const midLng = (lng1 + lng2) / 2;
    const bearing = calcBearing(positions[i], positions[i + 1]);
    arrowMarkers.push({ lat: midLat, lng: midLng, bearing, key: i });
  }

  return (
    <>
      <Polyline
        positions={positions}
        pathOptions={{ color: '#1d4ed8', weight: 4, opacity: 0.92, dashArray: '10, 8', lineCap: 'round' }}
      />
      {/* Start marker (green S) at first point */}
      <ZoomIndependentMarker position={positions[0]} icon={makeStartIcon()}>
        <Tooltip permanent={false}>
          <span style={{ fontSize: '11px' }}>
            Start: {validPoints[0].timestamp ? new Date(validPoints[0].timestamp).toLocaleString() : 'Point 1'}
          </span>
        </Tooltip>
      </ZoomIndependentMarker>
      {/* Directional arrows along the path */}
      {arrowMarkers.map(({ lat, lng, bearing, key }) => (
        <ZoomIndependentMarker key={`arrow_${key}`} position={[lat, lng]} icon={makeArrowIcon(bearing)} />
      ))}
      {/* End marker (blue E) at last point */}
      {positions.length > 1 && (
        <ZoomIndependentMarker position={positions[positions.length - 1]} icon={makeEndIcon()}>
          <Tooltip permanent={false}>
            <span style={{ fontSize: '11px' }}>
              End: {validPoints[validPoints.length - 1]?.timestamp ? new Date(validPoints[validPoints.length - 1].timestamp).toLocaleString() : `Point ${validPoints.length}`}
            </span>
          </Tooltip>
        </ZoomIndependentMarker>
      )}
      {validPoints.map((p, i) => (
        <React.Fragment key={`trail-point-${activeDeviceId}-${i}-${p.timestamp || 'na'}`}>
          {/* Pixel-sized dot (CircleMarker), not a metre-sized Circle: a 7 m Circle becomes a huge
              disc when zoomed into the trail. Stays a small point at every zoom. */}
          <CircleMarker
            center={[p.lat, p.lng]}
            radius={i === validPoints.length - 1 ? 5 : 3}
            pathOptions={{
              color: i === validPoints.length - 1 ? '#1d4ed8' : '#2563eb',
              fillColor: i === validPoints.length - 1 ? '#60a5fa' : '#93c5fd',
              fillOpacity: i === validPoints.length - 1 ? 0.95 : 0.8,
              weight: i === validPoints.length - 1 ? 2 : 1.5,
            }}
          />
          <ZoomIndependentMarker
            position={[p.lat, p.lng]}
            icon={makeTrailPointLabelIcon(i + 1, formatTrailPointTime(p.timestamp))}
          />
        </React.Fragment>
      ))}
      {/* Floating button to clear the trail */}
      <div style={{
        position: 'absolute', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
        zIndex: 1000, display: 'flex', gap: '8px',
      }}>
        <button
          onClick={() => dispatch(clearHistory())}
          style={{
            padding: '8px 18px', fontSize: '0.85rem', fontWeight: 600,
            background: '#fff', color: '#334155', border: '1px solid #e2e8f0',
            borderRadius: '10px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          }}
        >
          ✕ Clear Trail ({validPoints.length} pts)
        </button>
      </div>
    </>
  );
};

/**
 * SOS overlay: a pulsing red zone + badge for every LG Band currently in SOS.
 * Rendered OUTSIDE the cluster group and independent of the active filters, so an SOS is
 * visible even when LG Bands are clustered or filtered off. Clicking selects + navigates.
 */
// Red SOS bell with sound waves (same icon as the alert banner), inline for the Leaflet divIcon.
const SOS_BADGE_ICON_SVG = '<svg class="sos-overlay-badge-icon" viewBox="0 0 120 90" fill="none" aria-hidden="true">'
    + '<g stroke="#fff" stroke-width="7" stroke-linecap="round"><path d="M20 22 A46 46 0 0 0 20 68"/><path d="M31 28 A34 34 0 0 0 31 62"/><path d="M100 22 A46 46 0 0 1 100 68"/><path d="M89 28 A34 34 0 0 1 89 62"/></g>'
    + '<rect x="57" y="6" width="6" height="8" rx="1" fill="#fff"/><path d="M38 62 V40 a22 22 0 0 1 44 0 V62 H88 V70 H32 V62 Z" fill="#fff"/><path d="M50 73 a10 10 0 0 0 20 0 Z" fill="#fff"/>'
    + '<text x="60" y="52" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-weight="900" font-size="19" fill="#dc2626">SOS</text></svg>';

const SosOverlayLayer = ({ onLocate }) => {
    const adafruitLocations = useSelector((state) => state.locations.adafruitLocations);
    const beachLocations = useSelector((state) => state.locations.beachLocations);
    const sosDevices = useMemo(() => findSosLgBandDevices(adafruitLocations), [adafruitLocations]);
    if (sosDevices.length === 0) return null;
    return sosDevices.map((device) => {
        // Anchor to where the SOS was pressed, not the band's drifting live position.
        const anchor = getSosAnchorLatLng(device);
        const pos = anchor ? [anchor.lat, anchor.lng] : null;
        if (!pos) return null;
        const outside = isSosOutsideGeofences(anchor ? { last_latitude: anchor.lat, last_longitude: anchor.lng } : device, beachLocations);
        const badge = L.divIcon({
            className: 'sos-overlay-badge-wrap',
            html: `<div class="sos-overlay-badge${outside ? ' sos-overlay-badge--outside' : ''}" title="Click to locate">${SOS_BADGE_ICON_SVG}<span>SOS: Incident Alert</span></div>`,
            iconSize: [0, 0],
            iconAnchor: [0, 0],
        });
        const handlers = { click: () => { if (typeof onLocate === 'function') onLocate(device); } };
        return (
            <React.Fragment key={`sos-overlay-${device.device_id}`}>
                <Circle
                    center={pos}
                    radius={150}
                    pathOptions={{ className: 'sos-overlay-circle', color: '#e11d48', weight: 2, fillColor: '#ef4444', fillOpacity: 0.18 }}
                    eventHandlers={handlers}
                />
                <Marker position={pos} icon={badge} zIndexOffset={10000} eventHandlers={handlers} />
            </React.Fragment>
        );
    });
};

const MapComponent = React.memo(({
    devices = [],
    towerLocations = [],
    beachLocations = [],
    officeLocations = [],
    tritonLocations = [],
    aurusLocations = [],
    showTowers,
    showOffices,
    showTriton,
    showAurus,
    onMarkerClick,
    selectedDevice,
    showNameplates,
    showGeofences,
    onBeachGeofenceClick,
    shouldNavigateToSelectedDevice = false,
    permissions = {},
    selectedCity = '',
    selectedState = '',
    selectedCountry = 'in',
    setIsBoundaryLoading,
    showSwimZones = true,
    activeFilters = ['all']
}) => {
    // Get city boundary from Redux for geographic filtering
    const cityBoundary = useSelector(state => state.ui.cityBoundary);
    
    // Track navigation requests - use a state-based counter to force remount
    const [navigationCounter, setNavigationCounter] = React.useState(0);
    
    React.useEffect(() => {
        if (shouldNavigateToSelectedDevice && selectedDevice?.device_id) {
            setNavigationCounter(prev => {
                const newCount = prev + 1;
                console.log('🗺️ [MapComponent] Navigation requested, counter:', newCount);
                return newCount;
            });
        }
    }, [shouldNavigateToSelectedDevice, selectedDevice?.device_id]);
    
    const navigationKey = React.useMemo(() => {
        return `mapzoom-${selectedDevice?.device_id || 'none'}-${shouldNavigateToSelectedDevice ? 'nav' : 'no-nav'}-${navigationCounter}`;
    }, [shouldNavigateToSelectedDevice, selectedDevice?.device_id, navigationCounter]);
    
    // Helper for status styling
    const getStatusClass = (status) => {
        if (!status) return 'status-inactive';
        const lowerStatus = String(status).toLowerCase();
        if (lowerStatus === 'active') return 'status-active';
        if (lowerStatus === 'running') return 'status-running';
        if (lowerStatus === 'inactive' || lowerStatus === 'unknown') return 'status-inactive';
        return 'status-stopped';
    };

    // Memoize the navigation position to ensure it updates correctly
    const navigationPosition = useMemo(() => {
        console.log('🗺️ [MapComponent] Calculating navigation position:', {
            shouldNavigate: shouldNavigateToSelectedDevice,
            hasSelectedDevice: !!selectedDevice,
            deviceId: selectedDevice?.device_id,
            deviceName: selectedDevice?.displayName || selectedDevice?.name,
            lat: selectedDevice?.last_latitude,
            lng: selectedDevice?.last_longitude,
            latType: typeof selectedDevice?.last_latitude,
            lngType: typeof selectedDevice?.last_longitude,
            vehicleType: selectedDevice?.Vehicletype || selectedDevice?.vehicleType || selectedDevice?.device_type,
            fullSelectedDevice: selectedDevice
        });
        
        // Only calculate position if navigation is requested AND device is selected
        // Also check that it's an actual device (not a city/state selection marker)
        if (shouldNavigateToSelectedDevice && selectedDevice && 
            selectedDevice.device_id && 
            selectedDevice.device_id !== '__default__' && 
            !selectedDevice.device_id.startsWith('city_') && 
            !selectedDevice.device_id.startsWith('state_')) {
            // Try multiple possible coordinate property names
            let lat = selectedDevice.last_latitude || selectedDevice.latitude || 
                     (selectedDevice.location && selectedDevice.location.latitude) || 
                     (selectedDevice.coordinates && selectedDevice.coordinates.lat);
            let lng = selectedDevice.last_longitude || selectedDevice.longitude || 
                     (selectedDevice.location && selectedDevice.location.longitude) || 
                     (selectedDevice.coordinates && selectedDevice.coordinates.lng);
            
            // Parse if strings
            if (typeof lat === 'string') lat = parseFloat(lat);
            if (typeof lng === 'string') lng = parseFloat(lng);
            
            console.log('🗺️ [MapComponent] Parsed coordinates:', { 
                lat, 
                lng, 
                latIsNaN: isNaN(lat), 
                lngIsNaN: isNaN(lng),
                latType: typeof lat,
                lngType: typeof lng,
                originalLat: selectedDevice.last_latitude,
                originalLng: selectedDevice.last_longitude
            });
            
            // Validate coordinates inline (same logic as hasValidCoordinates)
            if (lat !== null && lat !== undefined && lng !== null && lng !== undefined &&
                !isNaN(lat) && !isNaN(lng) &&
                !(lat === 0 && lng === 0) &&
                lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                console.log('✅ [MapComponent] ✅✅✅ VALID Navigation position calculated:', { 
                    lat, 
                    lng, 
                    deviceId: selectedDevice.device_id,
                    deviceName: selectedDevice.displayName || selectedDevice.name,
                    shouldNavigate: shouldNavigateToSelectedDevice,
                    returning: [lat, lng]
                });
                return [lat, lng];
            } else {
                console.warn('❌ [MapComponent] Invalid coordinates for navigation:', { 
                    lat, 
                    lng, 
                    deviceId: selectedDevice.device_id,
                    deviceName: selectedDevice.displayName || selectedDevice.name,
                    latNull: lat === null,
                    latUndef: lat === undefined,
                    latNaN: isNaN(lat),
                    lngNull: lng === null,
                    lngUndef: lng === undefined,
                    lngNaN: isNaN(lng),
                    isZero: lat === 0 && lng === 0,
                    latRange: lat < -90 || lat > 90,
                    lngRange: lng < -180 || lng > 180,
                    fullDevice: selectedDevice,
                    allProps: Object.keys(selectedDevice)
                });
            }
        } else {
            if (!shouldNavigateToSelectedDevice) {
                console.log('⏸️ [MapComponent] Navigation not enabled (shouldNavigateToSelectedDevice is false)');
            }
            if (!selectedDevice) {
                console.log('⏸️ [MapComponent] No device selected (selectedDevice is null/undefined)');
            }
        }
        return null;
    }, [shouldNavigateToSelectedDevice, selectedDevice?.device_id, selectedDevice?.last_latitude, selectedDevice?.last_longitude, selectedDevice]);

    // A single, high-level log to show what the map is receiving.
    console.log('🗺️ [MapComponent] RENDERED with', {
        deviceCount: devices.length,
        selectedDeviceId: selectedDevice?.device_id,
        selectedDeviceName: selectedDevice?.displayName || selectedDevice?.name,
        shouldNavigateToSelectedDevice,
        navigationPosition,
        selectedDeviceLat: selectedDevice?.last_latitude,
        selectedDeviceLng: selectedDevice?.last_longitude,
        towerCount: towerLocations.length,
        beachCount: beachLocations.length,
        showNameplates,
        showGeofences,
    });

    // State for inventory modal
    const [inventoryModal, setInventoryModal] = useState({
        isOpen: false,
        tower: null
    });

    // Map reference for smart label management
    const mapRef = useRef(null);

    // Function to show inventory modal
    const showInventoryModal = (tower) => {
        setInventoryModal({
            isOpen: true,
            tower: tower
        });
    };

    // Function to close inventory modal
    const closeInventoryModal = () => {
        setInventoryModal({
            isOpen: false,
            tower: null
        });
    };
    // All hooks must be called before any early returns or conditional logic
    const [mapCenter] = useState([15.4989, 73.8278]); // Goa, India
    const [mapZoom] = useState(12);

    // Geofence drawing state
    const [isDrawingMode, setIsDrawingMode] = useState(false);
    const [showNamingModal, setShowNamingModal] = useState(false);
    const [pendingGeofence, setPendingGeofence] = useState(null);
    const [customGeofences, setCustomGeofences] = useState([]);
    const [showGeofenceManager, setShowGeofenceManager] = useState(false);

    // OSM Beach layer state

    // Clustering functionality removed

    // Debounce utility to prevent rapid updates
    const useDebounce = (value, delay) => {
        const [debouncedValue, setDebouncedValue] = useState(value);

        useEffect(() => {
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);

            return () => {
                clearTimeout(handler);
            };
        }, [value, delay]);

        return debouncedValue;
    };

    // Debounced geofence data to prevent rapid updates
    const debouncedBeachLocations = useDebounce(beachLocations, 100);
    const debouncedCustomGeofences = useDebounce(customGeofences, 100);

    // Helper function to validate device coordinates
    const hasValidCoordinates = useCallback((device) => {
        if (!device) return false;

        const lat = typeof device.last_latitude === 'string' ? parseFloat(device.last_latitude) : device.last_latitude;
        const lng = typeof device.last_longitude === 'string' ? parseFloat(device.last_longitude) : device.last_longitude;

        // Check if coordinates exist (including negative values)
        if (lat === null || lat === undefined || lng === null || lng === undefined) return false;
        if (isNaN(lat) || isNaN(lng)) return false;
        
        // Check if coordinates are not (0,0)
        if (lat === 0 && lng === 0) return false;

        // Check if coordinates are within valid ranges
        if (lat < -90 || lat > 90) return false;
        if (lng < -180 || lng > 180) return false;

        return true;
    }, []);

    // Helper function to normalize coordinates to a consistent format
    const normalizeCoordinates = useCallback((geofence) => {
        if (!geofence) return [];

        // Handle different coordinate formats
        let coords = [];
        if (geofence.polygon && Array.isArray(geofence.polygon)) {
            coords = geofence.polygon;
        } else if (geofence.coordinates && Array.isArray(geofence.coordinates)) {
            coords = geofence.coordinates;
        }

        // Convert to consistent [lat, lng] format and ensure precision
        return coords.map(point => {
            if (typeof point === 'object' && point.lat !== undefined && point.lng !== undefined) {
                // Round to 6 decimal places to prevent floating point precision issues
                return [Math.round(point.lat * 1000000) / 1000000, Math.round(point.lng * 1000000) / 1000000];
            }
            return point;
        }).filter(coord => Array.isArray(coord) && coord.length === 2);
    }, []);

    // Helper function to generate stable keys for geofences
    const generateStableKey = useCallback((geofence) => {
        if (geofence.id) return geofence.id;
        if (geofence.name) return `geofence_${geofence.name}`;
        return `geofence_${Date.now()}_${Math.random()}`;
    }, []);

    // Clustering functionality removed

    // Memoized devices with valid coordinates for map rendering
    const devicesWithValidCoordinates = useMemo(() => {
        if (!devices) return [];

        const validDevices = devices.filter(device => hasValidCoordinates(device));
        const invalidDevices = devices.filter(device => !hasValidCoordinates(device));

        // Log filtering results for debugging, but only if there are invalid devices.
        if (invalidDevices.length > 0) {
            console.warn(`🗺️ Map filtering: ${invalidDevices.length} devices were hidden from the map due to invalid coordinates.`);
        }

        return validDevices;
    }, [devices, hasValidCoordinates]);

    // Convert triton locations to device format and add to cluster when showTriton is true
    const tritonDevicesForCluster = useMemo(() => {
        if (!showTriton || !tritonLocations || !Array.isArray(tritonLocations)) return [];

        return tritonLocations
            .filter(triton => {
                // Check if triton has valid coordinates
                if (!triton.coordinates || typeof triton.coordinates.lat !== 'number' || typeof triton.coordinates.lng !== 'number') {
                    return false;
                }
                const lat = triton.coordinates.lat;
                const lng = triton.coordinates.lng;
                
                // Basic coordinate validation
                if (lat == null || lng == null || 
                    isNaN(lat) || isNaN(lng) ||
                    lat === 0 || lng === 0 ||
                    lat < -90 || lat > 90 ||
                    lng < -180 || lng > 180) {
                    return false;
                }
                
                // Apply geographic filtering if city/state is selected
                if (cityBoundary && (selectedCity || selectedState)) {
                    const devicePoint = [lat, lng];
                    return pointInGeoJSON(devicePoint, cityBoundary);
                }
                
                return true;
            })
            .map(triton => ({
                ...triton,
                device_id: triton.id || triton.deviceId,
                unique_key: triton.id || triton.deviceId,
                device_type: 'triton',
                last_latitude: triton.coordinates.lat,
                last_longitude: triton.coordinates.lng,
                displayName: triton.displayName || triton.name || triton.deviceId,
                name: triton.name || triton.deviceId,
                status: triton.status || 'unknown',
                battery_level: triton.battery,
                // Preserve triton-specific fields for popup
                triton_type: triton.triton_type,
                lastSeen: triton.lastSeen
            }));
    }, [showTriton, tritonLocations, cityBoundary, selectedCity, selectedState]);

    // Combine regular devices with triton devices for clustering
    const allDevicesForCluster = useMemo(() => {
        // Get device IDs from tritonDevicesForCluster to avoid duplicates
        const tritonDeviceIds = new Set(tritonDevicesForCluster.map(t => t.device_id || t.unique_key));
        
        // Filter out triton devices from regular devices if they're already in tritonDevicesForCluster
        // (to avoid duplicates if triton devices are already in the devices array)
        const regularDevices = devicesWithValidCoordinates.filter(device => {
            const deviceId = device.device_id || device.unique_key;
            // If showTriton is true and this device is a triton, check if it's in tritonDevicesForCluster
            if (showTriton && device.device_type === 'triton' && tritonDeviceIds.has(deviceId)) {
                return false; // Skip this device, it will be added from tritonDevicesForCluster
            }
            return true;
        });
        
        // Combine regular devices with triton devices
        return [...regularDevices, ...tritonDevicesForCluster];
    }, [devicesWithValidCoordinates, tritonDevicesForCluster, showTriton]);

    // Use SmartLabelManager for collision detection
    const { visibleLabels, zoomLevel } = SmartLabelManager({
        devices: devicesWithValidCoordinates,
        showNameplates,
        map: mapRef.current
    });

    // Memoized beach geofences with normalized coordinates
    const memoizedBeachGeofences = useMemo(() => {
        if (!showGeofences || !debouncedBeachLocations) return [];

        return debouncedBeachLocations.map(beach => {
            if (!beach.polygon || !Array.isArray(beach.polygon) || beach.polygon.length < 3) {
                return null;
            }

            const normalizedCoords = normalizeCoordinates(beach);
            if (normalizedCoords.length < 3) {
                return null;
            }

            const activeVehicles = beach.Vehicle_data ? Object.values(beach.Vehicle_data).filter(v => v.Exit_Time === null) : [];
            const isActive = activeVehicles.length > 0;

            return {
                ...beach,
                normalizedCoords,
                isActive,
                activeVehicles,
                stableKey: generateStableKey(beach)
            };
        }).filter(Boolean);
    }, [debouncedBeachLocations, showGeofences, normalizeCoordinates, generateStableKey]);

    // Memoized custom geofences with normalized coordinates
    const memoizedCustomGeofences = useMemo(() => {
        if (!showGeofences || !debouncedCustomGeofences) return [];

        return debouncedCustomGeofences.map(geofence => {
            const normalizedCoords = normalizeCoordinates(geofence);
            if (normalizedCoords.length < 3) {
                return null;
            }

            return {
                ...geofence,
                normalizedCoords,
                stableKey: generateStableKey(geofence)
            };
        }).filter(Boolean);
    }, [debouncedCustomGeofences, showGeofences, normalizeCoordinates, generateStableKey]);

    // Prevent map centering when beach geofences are clicked
    useEffect(() => {
        if (mapRef.current) {
            const map = mapRef.current;

            // Add a custom event listener to prevent map centering on beach polygon clicks
            const handleMapClick = (e) => {
                // Check if the click target is a beach polygon
                const target = e.originalEvent.target;
                if (target && target.classList && target.classList.contains('beach-polygon')) {
                    e.originalEvent.stopPropagation();
                    e.originalEvent.preventDefault();
                    return false;
                }
            };

            // Check if map is valid before adding event listener
            try {
                if (map.getContainer() && map.getContainer().parentNode) {
                    map.on('click', handleMapClick);
                }
            } catch (error) {
                // This can happen on fast reloads, it's safe to ignore.
            }

            return () => {
                // Safe cleanup - check if map still exists
                try {
                    if (map && map.getContainer && map.getContainer()) {
                        map.off('click', handleMapClick);
                    }
                } catch (error) {
                    // This can happen on fast reloads, it's safe to ignore.
                }
            };
        }
    }, []);

    // Add cleanup effect for component unmount
    useEffect(() => {
        // Add global error handler for Leaflet errors
        const originalError = console.error;
        console.error = (...args) => {
            const errorString = args.join(' ');
            if (errorString.includes('_leaflet_pos')) {
                // This is a known Leaflet issue with hot-reloading, we can safely ignore it.
                return;
            }
            originalError.apply(console, args);
        };

        return () => {
            // Restore original error handler
            console.error = originalError;

            // Cleanup map reference on component unmount
            if (mapRef.current) {
                try {
                    const map = mapRef.current;
                    // More thorough cleanup
                    if (map) {
                        // Stop all map operations
                        map.stop();

                        // Remove all event listeners
                        map.off();

                        // Remove the map
                        if (map.remove) {
                            map.remove();
                        }
                    }
                } catch (error) {
                    // It's normal for this to fail on fast reloads, so we can ignore it.
                }
                mapRef.current = null;
            }
        };
    }, []);

    // Validation check after all hooks are called
    if (!Array.isArray(devices)) {
        console.error('MapComponent: devices prop must be an array');
        return (
            <div style={{
                height: '100vh',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5'
            }}>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <h3>Map Configuration Error</h3>
                    <p>Invalid device data provided to map component.</p>
                </div>
            </div>
        );
    }

    // Handle geofence creation from drawing
    const handleGeofenceCreated = (geofenceData) => {
        if (geofenceData.action === 'delete') {
            // Handle deletion - remove from local state
            setCustomGeofences(prev => prev.filter(g => g.layer !== geofenceData.layer));
            return;
        }

        setPendingGeofence(geofenceData);
        setShowNamingModal(true);
    };


    // Save geofence to Firestore
    const saveGeofenceToFirestore = async (geofenceData) => {
        try {
            const geofenceDoc = {
                name: geofenceData.name,
                description: geofenceData.description || '',
                polygon: geofenceData.coordinates, // Save as polygon for beaches collection
                type: 'custom_beach',
                created_at: serverTimestamp(),
                created_by: 'user', // You can replace this with actual user ID
                vertices: geofenceData.coordinates.length,
                is_active: true,
                area: 'Custom Area' // Add area field for beaches collection
            };

            // Use the exact geofence name as the document ID
            const docId = geofenceData.name;

            const docRef = doc(db, 'all_assets/beachs/Beaches', docId);
            await setDoc(docRef, geofenceDoc);

            // Add to local state
            const newGeofence = {
                id: docId,
                ...geofenceDoc,
                created_at: new Date().toISOString(),
                layer: geofenceData.layer // Keep reference to the layer for deletion
            };

            setCustomGeofences(prev => [...prev, newGeofence]);

            console.log('Geofence saved successfully to all_assets/beachs/Beaches collection with ID:', docId);
            return newGeofence;
        } catch (error) {
            console.error('Error saving geofence:', error);
            throw error;
        }
    };

    // Delete geofence from Firestore
    const deleteGeofenceFromFirestore = async (geofenceId, geofenceName) => {
        try {
            // Delete from Firestore database
            const docRef = doc(db, 'all_assets/beachs/Beaches', geofenceName);
            await deleteDoc(docRef);

            // Remove from local state
            setCustomGeofences(prev => prev.filter(g => g.id !== geofenceId));

            console.log('Geofence deleted successfully:', geofenceName);
            return true;

        } catch (error) {
            console.error('Error deleting geofence from Firestore:', error);
            throw error;
        }
    };

    // Delete beach geofence from Firestore
    const deleteBeachGeofenceFromFirestore = async (beachId, beachName) => {
        try {
            // Delete from Firestore database
            const docRef = doc(db, 'all_assets/beachs/Beaches', beachName);
            await deleteDoc(docRef);

            console.log('Beach geofence deleted successfully:', beachName);
            return true;

        } catch (error) {
            console.error('Error deleting beach geofence from Firestore:', error);
            throw error;
        }
    };

    // Toggle drawing mode
    const toggleDrawingMode = () => {
        setIsDrawingMode(!isDrawingMode);
    };

    // Toggle geofence manager
    const toggleGeofenceManager = () => {
        setShowGeofenceManager(!showGeofenceManager);
    };

    // Clustering functionality removed

    return (
        <MapErrorBoundary>
            {/* Map Controls - Only show for admin users */}
            {permissions?.isAdmin === true && (
                <div className="geofence-drawing-controls">
                    <button
                        className={`drawing-toggle-btn ${isDrawingMode ? 'active' : ''}`}
                        onClick={toggleDrawingMode}
                        title={isDrawingMode ? 'Exit Drawing Mode' : 'Enter Drawing Mode'}
                    >
                        {isDrawingMode ? '✕' : '✏️'} {isDrawingMode ? 'Exit Drawing' : 'Draw Geofence'}
                    </button>
                    <button
                        className="geofence-manager-btn"
                        onClick={toggleGeofenceManager}
                        title="Manage Geofences"
                    >
                        🗂️ Manage Geofences
                    </button>
                </div>
            )}

            <MapContainer
                key="main-map" // Add key for stable identity
                center={mapCenter}
                zoom={mapZoom}
                style={{ height: '100%', width: '100%' }}
                ref={mapRef}
                dragging={true}
                scrollWheelZoom={true}
                wheelDebounceTime={20}
                wheelPxPerZoomLevel={120}
                doubleClickZoom={true}
                zoomSnap={0.25}
                zoomDelta={0.5}
                zoomAnimation={true}
                zoomAnimationThreshold={4}
                fadeAnimation={true}
                markerZoomAnimation={true}
                touchZoom={true}
                bounceAtZoomLimits={false}
                inertia={true}
                inertiaDeceleration={3000}
                easeLinearity={0.2}
                boxZoom={true}
                keyboard={true}
                maxZoom={23}
                attributionControl={false}
                whenReady={(mapInstance) => {
                    try {
                        // Ensure map is properly initialized
                        console.log('🗺️ Map ready and initialized');

                        // Store map instance safely
                        if (mapRef.current !== mapInstance) {
                            mapRef.current = mapInstance;
                        }

                        // Add error handling for map operations
                        if (mapInstance && mapInstance.invalidateSize) {
                            // Force initial render to ensure DOM elements are properly set
                            setTimeout(() => {
                                try {
                                    mapInstance.invalidateSize();
                                } catch (sizeError) {
                                    // This is a minor warning, not a critical error.
                                }
                            }, 100);
                        }
                    } catch (error) {
                        console.error('Map initialization error:', error);
                    }
                }}
                whenCreated={(mapInstance) => {
                    try {
                        console.log('🗺️ Map created');
                        if (mapInstance) {
                            mapRef.current = mapInstance;
                        }
                    } catch (error) {
                        console.error('Map creation error:', error);
                    }
                }}
            >
                <MapZoom
                    key={navigationKey}
                    position={navigationPosition}
                    zoomLevel={selectedDevice && hasValidCoordinates(selectedDevice)
                        ? ((selectedDevice.device_type === 'lg_band' || selectedDevice.device_type === 'adafruit') ? LG_BAND_LOCATE_ZOOM : 18)
                        : mapZoom}
                    forceNavigate={shouldNavigateToSelectedDevice}
                />

                <FollowSelectedLgBand devices={devices} selectedDevice={selectedDevice} />

                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
                    maxZoom={23}
                    maxNativeZoom={19}
                    updateWhenZooming={true}
                    updateWhenIdle={false}
                    updateInterval={120}
                    keepBuffer={8}
                    detectRetina={false}
                />

                {/* City/State Boundary Layer */}
                <CityStateBoundaryLayer cityName={selectedCity} stateName={selectedState} countryCode={selectedCountry} cityZoomLevel={11} defaultCenter={mapCenter} defaultZoom={mapZoom} onLoadingChange={typeof setIsBoundaryLoading === 'function' ? setIsBoundaryLoading : undefined} disableDefaultRecenter={false} />

                {/* Geofence Drawing Component */}
                {isDrawingMode && (
                    <GeofenceDrawer
                        onGeofenceCreated={handleGeofenceCreated}
                        onGeofenceDeleted={handleGeofenceCreated}
                    />
                )}

                {/* Wrap markers in MarkerClusterGroup */}
                <MarkerClusterGroup
                    iconCreateFunction={createClusterCustomIcon}
                    showCoverageOnHover={true}
                    spiderfyOnMaxZoom={true}
                    disableClusteringAtZoom={16}
                    chunkedLoading={true}
                    chunkInterval={80}
                    chunkDelay={20}
                    animate={true}
                    animateAddingMarkers={false}
                    removeOutsideVisibleBounds={true}
                >
                    {/* SwimZone Layer (clustered) - only show if 'all' is selected or 'swimzone' is in activeFilters */}
                    <SwimZoneLayer visible={showSwimZones && (activeFilters.includes('all') || activeFilters.includes('swimzone'))} />

                    {/* Render Devices using the new DeviceMarker component */}
                    {allDevicesForCluster.map(device => {
                        const deviceId = device.unique_key || device.device_id;
                        return (
                            <DeviceMarker
                                key={deviceId}
                                device={device}
                                showNameplates={showNameplates}
                                // Pass the visibility logic directly to the marker
                                shouldShowLabel={visibleLabels.has(deviceId)}
                            />
                        );
                    })}

                    {/* Render Towers (clustered) */}
                    {showTowers && towerLocations
                        .filter(item => item.render_type === 'tower')
                        .filter(item => item.coordinates && typeof item.coordinates.lat === 'number' && typeof item.coordinates.lng === 'number')
                        .map(item => (
                            <ZoomIndependentMarker
                                key={item.id}
                                position={[item.coordinates.lat, item.coordinates.lng]}
                                icon={createTowerIcon(item, showNameplates)}
                                eventHandlers={{
                                    click: () => {
                                        console.log(`🗼 Tower '${item.name}' clicked.`);
                                    }
                                }}
                            >
                                <Popup className="custom-popup">
                                    <h4 title={item.name}>{item.name}</h4>
                                    <hr />
                                    <div className="popup-content">
                                        <div className="info-row">
                                            <span className="info-label">Type</span>
                                            <span className="info-value">Lifeguard Tower</span>
                                        </div>
                                        {item.beach && item.beach !== 'N/A' && (
                                            <div className="info-row">
                                                <span className="info-label">Beach</span>
                                                <span className="info-value">{item.beach}</span>
                                            </div>
                                        )}
                                        {permissions?.viewLgCount === true && item.lg_count !== undefined && (
                                            <div className="info-row">
                                                <span className="info-label">Lifeguard Count</span>
                                                <span className="info-value">{item.lg_count}</span>
                                            </div>
                                        )}
                                        {permissions?.viewInventory === true && (
                                            <div className="info-row">
                                                <span className="info-label">Actions</span>
                                                <button
                                                    className="inventory-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        showInventoryModal(item);
                                                    }}
                                                    style={{
                                                        background: '#004aad',
                                                        color: 'white',
                                                        border: 'none',
                                                        padding: '6px 12px',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '12px',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    📦 View Inventory ({item.inventory?.total_items || 0} items)
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </Popup>
                            </ZoomIndependentMarker>
                        ))}
                </MarkerClusterGroup>

                {/* SOS overlay: non-clustered, always visible, independent of filters */}
                <SosOverlayLayer onLocate={onMarkerClick} />

                {/* Device History Trail Polyline */}
                <DeviceHistoryTrail />

                {/* Render Offices */}
                {showOffices && officeLocations && officeLocations.map(office => {
                    if (!office.coordinates || typeof office.coordinates.lat !== 'number' || typeof office.coordinates.lng !== 'number') {
                        console.warn('Skipping office with invalid coordinates:', office);
                        return null;
                    }

                    return (
                        <React.Fragment key={office.id}>
                            <ZoomIndependentMarker
                                position={[office.coordinates.lat, office.coordinates.lng]}
                                icon={officeIcon}
                            >
                                <Popup className="custom-popup">
                                    <h4 title={office.name}>{office.name}</h4>
                                    <hr />
                                    <div className="popup-content">
                                        <div className="info-row">
                                            <span className="info-label">Type</span>
                                            <span className="info-value">Office Location</span>
                                        </div>
                                        {office.area && (
                                            <div className="info-row">
                                                <span className="info-label">Area</span>
                                                <span className="info-value">{office.area}</span>
                                            </div>
                                        )}
                                        {office.category && (
                                            <div className="info-row">
                                                <span className="info-label">Category</span>
                                                <span className="info-value">{office.category}</span>
                                            </div>
                                        )}
                                    </div>
                                </Popup>
                            </ZoomIndependentMarker>
                            {showGeofences && (
                                <Circle
                                    center={[office.coordinates.lat, office.coordinates.lng]}
                                    radius={office.radius || 300} // Use office radius or default to 300m
                                    pathOptions={{ color: '#f39c12', weight: 2, fillOpacity: 0.1 }}
                                />
                            )}
                        </React.Fragment>
                    );
                })}

                {/* Triton devices are now rendered in the cluster above */}
                {/* This section is kept for backward compatibility but triton devices are clustered */}

                {/* Render Aurus Locations */}
                {showAurus && aurusLocations && aurusLocations.map(aurus => {
                    if (!aurus.coordinates || typeof aurus.coordinates.lat !== 'number' || typeof aurus.coordinates.lng !== 'number') {
                        console.warn('Skipping aurus with invalid coordinates:', aurus);
                        return null;
                    }

                    return (
                        <React.Fragment key={aurus.id}>
                            <ZoomIndependentMarker
                                position={[aurus.coordinates.lat, aurus.coordinates.lng]}
                                icon={aurusIcon}
                            >
                                <Popup className="custom-popup">
                                    <h4 title={aurus.name}>{aurus.name}</h4>
                                    <hr />
                                    <div className="popup-content">
                                        <div className="info-row">
                                            <span className="info-label">Type</span>
                                            <span className="info-value">Aurus Device</span>
                                        </div>
                                        <div className="info-row">
                                            <span className="info-label">Device ID</span>
                                            <span className="info-value">{aurus.deviceId}</span>
                                        </div>
                                        <div className="info-row">
                                            <span className="info-label">Battery</span>
                                            <span className={`battery-indicator ${aurus.battery > 50 ? 'battery-high' : aurus.battery > 20 ? 'battery-medium' : 'battery-low'}`}>
                                                🔋 {aurus.battery}%
                                            </span>
                                        </div>
                                        <div className="info-row">
                                            <span className="info-label">Status</span>
                                            <span className={`status-badge ${getStatusClass(aurus.status)}`}>
                                                {aurus.status || 'N/A'}
                                            </span>
                                        </div>
                                        {aurus.lastSeen && (
                                            <div className="info-row">
                                                <span className="info-label">Last Seen</span>
                                                <span className="info-value">{aurus.lastSeen.toLocaleString()}</span>
                                            </div>
                                        )}
                                    </div>
                                </Popup>
                            </ZoomIndependentMarker>
                        </React.Fragment>
                    );
                })}

                {/* Render Towers and Geofences */}
                {showTowers && towerLocations.map(item => {
                    if (!item.coordinates || typeof item.coordinates.lat !== 'number' || typeof item.coordinates.lng !== 'number') {
                        return null;
                    }

                    // Render a circle for consolidated geofences
                    if (item.render_type === 'geofence' && item.radius && showGeofences) {
                        const activeVehicles = item.Vehicle_data ? Object.values(item.Vehicle_data).filter(v => v.Exit_Time === null) : [];
                        const isActive = activeVehicles.length > 0;

                        return (
                            <Circle
                                key={item.id}
                                center={[item.coordinates.lat, item.coordinates.lng]}
                                radius={item.radius}
                                pathOptions={{
                                    color: isActive ? '#ff4757' : '#e74c3c',
                                    weight: isActive ? 3 : 2,
                                    fillOpacity: isActive ? 0.4 : 0.1
                                }}
                                className={isActive ? 'geofence-active' : ''}
                            >
                                <Popup className="custom-popup">
                                    <h4 title={item.name}>{item.name}</h4>
                                    <hr />
                                    <div className="popup-content">
                                        <div className="info-row">
                                            <span className="info-label">Radius</span>
                                            <span className="info-value">{Math.round(item.radius)}m</span>
                                        </div>
                                        <div className="info-row">
                                            <span className="info-label">Vehicles Inside</span>
                                            <span className={`status-badge ${isActive ? 'status-running' : 'status-stopped'}`}>
                                                {activeVehicles.length}
                                            </span>
                                        </div>
                                        {isActive && (
                                            <div className="info-row">
                                                <span className="info-label">Active Vehicles</span>
                                                <div className="info-value">
                                                    {activeVehicles.map(vehicle => (
                                                        <div key={vehicle.Vehicle_name} style={{ marginBottom: '4px', fontSize: '12px' }}>
                                                            <strong>{vehicle.Vehicle_name}</strong> ({vehicle.Vehicle_Type})
                                                            <br />
                                                            <small style={{ color: '#666' }}>Entered: {new Date(vehicle.Entry_Time).toLocaleTimeString()}</small>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Popup>
                                <Tooltip
                                    permanent={false}
                                    direction="center"
                                    className="geofence-tooltip"
                                >
                                    <strong>{item.name}</strong>
                                </Tooltip>
                            </Circle>
                        );
                    }

                    // Handle beach geofences (rendered as polygons)
                    if (item.render_type === 'beach') {
                        const normalizedCoords = normalizeCoordinates(item.polygon);
                        if (!normalizedCoords) return null;
                        return (
                            <Polygon
                                key={item.id}
                                positions={normalizedCoords}
                                pathOptions={{ color: '#004aad', weight: 2, fillOpacity: 0.3 }}
                            >
                                <Tooltip permanent={false} direction="center" className="geofence-tooltip">
                                    <strong>{item.name}</strong><br />
                                    <small>Beach Geofence</small>
                                </Tooltip>
                            </Polygon>
                        );
                    }

                    // Towers are rendered inside MarkerClusterGroup above (clustered)
                    if (item.render_type === 'tower') {
                        return null;
                    }
                    // Skip any items that don't match the expected render types
                    return null;
                })}

                {/* Render Beach Polygons */}
                {memoizedBeachGeofences.map(beach => (
                    <Polygon
                        key={beach.stableKey}
                        positions={beach.normalizedCoords}
                        pathOptions={{
                            color: '#8e44ad',
                            weight: beach.isActive ? 4 : 2,
                            fillOpacity: beach.isActive ? 0.6 : 0.4,
                            fillColor: '#8e44ad',
                            smoothFactor: 1.0,
                            noClip: false
                        }}
                        className={beach.isActive ? 'beach-polygon geofence-active' : 'beach-polygon'}
                        eventHandlers={{
                            click: (e) => {
                                // Prevent event bubbling and default behavior to avoid map centering
                                e.originalEvent.stopPropagation();
                                e.originalEvent.preventDefault();

                                // Stop the event from propagating to the map
                                L.DomEvent.stopPropagation(e.originalEvent);
                                L.DomEvent.preventDefault(e.originalEvent);

                                if (onBeachGeofenceClick) {
                                    // Find devices that are currently inside this beach geofence
                                    const devicesInBeach = devices.filter(device => {
                                        // Use the same coordinate validation as map rendering
                                        if (!hasValidCoordinates(device)) return false;

                                        // Simple point-in-polygon check
                                        const point = [device.last_latitude, device.last_longitude];
                                        return isPointInPolygon(point, beach.polygon);
                                    });

                                    onBeachGeofenceClick(beach, devicesInBeach);
                                }

                                // Return false to prevent any default behavior
                                return false;
                            }
                        }}
                    />
                ))}

                {/* Render Custom Geofences */}
                {memoizedCustomGeofences.map(geofence => (
                    <Polygon
                        key={geofence.stableKey}
                        positions={geofence.normalizedCoords}
                        pathOptions={{
                            color: '#28a745',
                            weight: 2,
                            fillOpacity: 0.2,
                            fillColor: '#28a745',
                            smoothFactor: 1.0,
                            noClip: false
                        }}
                    >
                        <Popup>
                            <div className="custom-popup">
                                <h4 title={geofence.name}>{geofence.name}</h4>
                                <p>Type: Custom Beach Geofence</p>
                                <p>Vertices: {geofence.vertices}</p>
                                {geofence.description && <p>Description: {geofence.description}</p>}
                                <p>Created: {new Date(geofence.created_at).toLocaleDateString()}</p>
                                <hr />
                                <button
                                    className="delete-geofence-btn"
                                    onClick={() => {
                                        // Remove from local state
                                        setCustomGeofences(prev => prev.filter(g => g.id !== geofence.id));
                                        // TODO: Add Firestore deletion if needed
                                    }}
                                >
                                    Delete Geofence
                                </button>
                            </div>
                        </Popup>
                    </Polygon>
                ))}

            </MapContainer>

            {/* Geofence Naming Modal */}
            <GeofenceNamingModal
                isOpen={showNamingModal}
                onClose={() => {
                    setShowNamingModal(false);
                    setPendingGeofence(null);
                }}
                onSave={saveGeofenceToFirestore}
                geofenceData={pendingGeofence}
            />

            <GeofenceManager
                isOpen={showGeofenceManager}
                onClose={() => setShowGeofenceManager(false)}
                customGeofences={customGeofences}
                beachLocations={beachLocations}
                onDeleteGeofence={deleteGeofenceFromFirestore}
                onDeleteBeachGeofence={deleteBeachGeofenceFromFirestore}
            />

            {/* Inventory Panel - Top Right */}
            {inventoryModal.isOpen && (
                <div
                    className="inventory-panel-overlay"
                    style={{
                        position: 'fixed',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        backgroundColor: 'transparent',
                        zIndex: 10000,
                        pointerEvents: 'none'
                    }}
                >
                    <div
                        className="inventory-panel"
                        style={{
                            position: 'absolute',
                            top: '80px',
                            right: '20px',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '12px',
                            width: '380px',
                            maxHeight: 'calc(100vh - 100px)',
                            overflow: 'hidden',
                            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                            display: 'flex',
                            flexDirection: 'column',
                            pointerEvents: 'auto',
                            border: '1px solid #e9ecef',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)'
                        }}
                    >
                        {/* Panel Header */}
                        <div className="inventory-panel-header" style={{
                            background: 'linear-gradient(135deg, #004aad 0%, #0062e6 100%)',
                            color: 'white',
                            padding: '8px 12px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderRadius: '8px 8px 0 0'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
                                📦 {inventoryModal.tower?.name}
                            </h3>
                            <button
                                onClick={closeInventoryModal}
                                className="close-button"
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '1.2rem',
                                    cursor: 'pointer',
                                    padding: '0',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '50%',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                                ×
                            </button>
                        </div>

                        {/* Panel Content */}
                        <div className="inventory-panel-content" style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '12px',
                            color: '#000000'
                        }}>
                            {/* Tower Info Card */}
                            <div className="tower-info-card" style={{
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #e9ecef',
                                borderRadius: '8px',
                                padding: '12px',
                                marginBottom: '15px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '8px',
                                    paddingBottom: '8px',
                                    borderBottom: '1px solid #dee2e6'
                                }}>
                                    <span style={{ fontSize: '16px', marginRight: '8px' }}>🏗️</span>
                                    <span style={{ fontWeight: '700', color: '#000000', fontSize: '16px' }}>
                                        {inventoryModal.tower?.name}
                                    </span>
                                </div>

                                {inventoryModal.tower?.beach && inventoryModal.tower.beach !== 'N/A' && (
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                        <span style={{ fontSize: '14px', marginRight: '8px' }}>🏖️</span>
                                        <span style={{ fontWeight: '600', color: '#495057', fontSize: '14px' }}>
                                            <strong>Beach:</strong> {inventoryModal.tower.beach}
                                        </span>
                                    </div>
                                )}

                                {permissions?.viewLgCount === true && (
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                        <span style={{ fontSize: '14px', marginRight: '8px' }}>👥</span>
                                        <span style={{ color: '#495057', fontSize: '14px' }}>
                                            <strong>Lifeguards:</strong> {inventoryModal.tower?.lg_count || 'N/A'}
                                        </span>
                                    </div>
                                )}

                                {permissions?.viewInventory === true && (
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <span style={{ fontSize: '14px', marginRight: '8px' }}>📋</span>
                                        <span style={{ color: '#495057', fontSize: '14px' }}>
                                            <strong>Total Items:</strong> {inventoryModal.tower?.inventory?.total_items || 0}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Inventory Items */}
                            {permissions?.viewInventory === true && inventoryModal.tower?.inventory && inventoryModal.tower.inventory.total_items > 0 ? (
                                <div>
                                    <h4 style={{
                                        margin: '0 0 15px 0',
                                        color: '#000000',
                                        fontSize: '18px',
                                        fontWeight: '700',
                                        borderBottom: '3px solid #004aad',
                                        paddingBottom: '8px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                        <span style={{ marginRight: '8px', fontSize: '16px' }}>📋</span>
                                        Inventory Items
                                    </h4>
                                    <div style={{
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        paddingRight: '4px',
                                        scrollbarWidth: 'thin',
                                        scrollbarColor: '#004aad transparent'
                                    }}>
                                        {inventoryModal.tower.inventory.items && inventoryModal.tower.inventory.items.map((itemName, index) => {
                                            const itemCount = inventoryModal.tower.inventory.item_counts?.[itemName] || 0;
                                            return (
                                                <div
                                                    key={index}
                                                    className="inventory-item-card"
                                                    style={{
                                                        border: '1px solid #e9ecef',
                                                        borderRadius: '8px',
                                                        padding: '12px',
                                                        marginBottom: '8px',
                                                        backgroundColor: '#ffffff',
                                                        transition: 'all 0.3s ease',
                                                        cursor: 'default',
                                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.boxShadow = '0 4px 12px rgba(0, 74, 173, 0.2)';
                                                        e.target.style.borderColor = '#004aad';
                                                        e.target.style.transform = 'translateY(-2px)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                                                        e.target.style.borderColor = '#e9ecef';
                                                        e.target.style.transform = 'translateY(0)';
                                                    }}
                                                >
                                                    <div style={{
                                                        fontWeight: '700',
                                                        marginBottom: '8px',
                                                        color: '#000000',
                                                        fontSize: '15px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between'
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <span style={{ marginRight: '8px', fontSize: '14px' }}>📦</span>
                                                            {itemName}
                                                        </div>
                                                        <div style={{
                                                            backgroundColor: '#004aad',
                                                            color: 'white',
                                                            padding: '4px 8px',
                                                            borderRadius: '12px',
                                                            fontSize: '12px',
                                                            fontWeight: '600'
                                                        }}>
                                                            {itemCount}
                                                        </div>
                                                    </div>
                                                    <div style={{ fontSize: '13px', color: '#495057' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <span style={{ marginRight: '6px', fontSize: '12px' }}>📊</span>
                                                            <strong>Quantity:</strong> {itemCount} {itemCount === 1 ? 'item' : 'items'}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '40px 20px',
                                    color: '#6c757d',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '12px',
                                    border: '2px dashed #dee2e6',
                                    marginTop: '10px'
                                }}>
                                    <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.7 }}>📭</div>
                                    <h5 style={{
                                        margin: '0 0 8px 0',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: '#495057'
                                    }}>
                                        No Inventory Items
                                    </h5>
                                    <p style={{
                                        margin: 0,
                                        fontSize: '14px',
                                        fontWeight: '400',
                                        color: '#6c757d'
                                    }}>
                                        No inventory items available for this tower.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </MapErrorBoundary>
    );
});

export default MapComponent;
